import { useId, useMemo, useState, type PointerEvent } from 'react';
import type { LedgerPoint } from '../../lib/bankrollLedger';
import { formatUsd } from '../../lib/format';
import styles from './BankrollChart.module.css';

const WIDTH = 320;
const HEIGHT = 96;
const PAD_Y = 6;

const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

interface Plotted {
  x: number;
  y: number;
  point: LedgerPoint;
}

/**
 * A strategy's paper bankroll after each bet, one line per run. A bust (the
 * bankroll could not cover the next bet) is a dashed marker where one run
 * ends and the next starts back at the starting pool, so a reset is the
 * most visible thing on the chart rather than something it hides. One series, so no legend: the caption names it.
 */
export function BankrollChart({
  series,
  startingPool,
  fromStart,
}: {
  series: readonly LedgerPoint[];
  startingPool: number;
  /** The series begins with the strategy's very first bet (not a trimmed tail). */
  fromStart: boolean;
}) {
  const titleId = useId();
  const [active, setActive] = useState<number | null>(null);

  const chart = useMemo(() => {
    if (series.length < 2) return null;
    const max = Math.max(startingPool, ...series.map((p) => p[1]));
    const toY = (value: number) => PAD_Y + (1 - value / max) * (HEIGHT - PAD_Y * 2);
    const toX = (index: number) => (index / (series.length - 1)) * WIDTH;
    const plotted: Plotted[] = series.map((point, i) => ({
      x: toX(i),
      y: toY(point[1]),
      point,
    }));

    // One path per run; each new run starts from the starting pool at the
    // x of the bet that ended the one before.
    const paths: string[] = [];
    const busts: number[] = [];
    let d = fromStart ? `M0,${toY(startingPool).toFixed(2)} ` : '';
    plotted.forEach((p, i) => {
      const previous = plotted[i - 1];
      if (previous && previous.point[2] !== p.point[2]) {
        paths.push(d.trim());
        busts.push(previous.x);
        d = `M${previous.x.toFixed(2)},${toY(startingPool).toFixed(2)} `;
      }
      d += `${d ? 'L' : 'M'}${p.x.toFixed(2)},${p.y.toFixed(2)} `;
    });
    paths.push(d.trim());

    return { plotted, paths, busts, startY: toY(startingPool) };
  }, [series, startingPool, fromStart]);

  if (!chart) return null;

  const last = series[series.length - 1];
  const first = series[0];
  const shown = active !== null ? series[Math.min(active, series.length - 1)] : last;
  const bustCount = chart.busts.length;
  const summary = `Bankroll after each of the last ${series.length} bets, from ${formatUsd(first[1])} on ${shortDate.format(first[0])} to ${formatUsd(last[1])} on ${shortDate.format(last[0])}. ${
    bustCount === 0
      ? 'No busts in this stretch.'
      : `${bustCount} ${bustCount === 1 ? 'bust' : 'busts'} in this stretch, each topped back up to ${formatUsd(startingPool)}.`
  }`;

  const scrub = (event: PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (rect.width === 0) return;
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    setActive(Math.round(ratio * (series.length - 1)));
  };
  const activePoint = active !== null ? chart.plotted[active] : null;

  return (
    <figure className={styles.figure}>
      <figcaption className={styles.readout}>
        <span className={styles.readoutLabel}>
          {active !== null
            ? `${shortDate.format(shown[0])}, run ${shown[2]}`
            : `Bankroll after each bet, last ${series.length.toLocaleString()}`}
        </span>
        <span className={styles.readoutValue}>{formatUsd(shown[1])}</span>
      </figcaption>
      <div className={styles.plot}>
        <svg
          role="img"
          aria-labelledby={titleId}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="none"
          className={styles.svg}
          onPointerMove={scrub}
          onPointerDown={scrub}
          onPointerLeave={() => setActive(null)}
          onPointerCancel={() => setActive(null)}
        >
          <title id={titleId}>{summary}</title>
          <line
            x1={0}
            x2={WIDTH}
            y1={chart.startY}
            y2={chart.startY}
            className={styles.reference}
          />
          {chart.busts.map((x) => (
            <line key={x} x1={x} x2={x} y1={0} y2={HEIGHT} className={styles.bust} />
          ))}
          {chart.paths.map((d, i) => (
            <path key={i} d={d} className={styles.line} fill="none" />
          ))}
          {activePoint && (
            <line
              x1={activePoint.x}
              x2={activePoint.x}
              y1={0}
              y2={HEIGHT}
              className={styles.cursor}
            />
          )}
        </svg>
        {/* In a gutter beside the plot, so it never sits on the line. */}
        <span
          className={styles.axisLabel}
          style={{ top: `${(chart.startY / HEIGHT) * 100}%` }}
        >
          {formatUsd(startingPool).replace('.00', '')}
        </span>
      </div>
      {bustCount > 0 && (
        <p className={styles.key}>
          <span className={styles.keyMark} aria-hidden="true" />
          Bust: too small for the next bet, topped back up to{' '}
          {formatUsd(startingPool).replace('.00', '')}
        </p>
      )}
    </figure>
  );
}
